import { OrganizationAllowedWebsiteSource, Status } from '@pillar-portal/database/dist/enums';
import { z } from 'zod';
import {
    AllowedWebsiteSchemaUnmanaged,
    AllowedWebsiteWriteSchemaUnmanaged,
    RpaBuilderWebsiteConfigSchema,
    StoredUnmanagedAllowedWebsiteParser,
    StoredUnmanagedAllowedWebsiteStorageV2Schema,
    serializeUnmanagedAllowedWebsiteV2,
    type RpaBuilderWebsiteConfig,
    type StoredUnmanagedAllowedWebsite,
    type UnmanagedAllowedWebsite,
} from './backend_config.types';
import { defaultIntegratedWebsiteSystemIds } from './backend_config.constants';
import { OpaqueCursorPaginationSchema, type OpaqueCursorPagination } from './authorization.types';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import type { RbsWebsite, RbsWebsiteStorageV1 } from './rbs-website.types';
import {
    AuthorizationOpaqueCursorQuerySchema,
    AuthorizationPaginationLimitQuerySchema,
} from './schemas/authorization.schemas';
import { SearchTermsSchema } from './schemas/omnisearch.schemas';
import {
    RbsWebsiteSchema,
    StoredRbsWebsiteParser,
    StoredRbsWebsiteStorageV1Schema,
    serializeRbsWebsiteV1,
} from './schemas/rbs-website.schemas';
import { buildStoredJsonValueParser, defineStoredJsonContract } from './stored-json-contract.types';
import { ThirdPartySystemId } from './types/third-party-system.enums';

export type AllowedWebsiteTemplateSystemId =
    | typeof ThirdPartySystemId.GENERIC
    | (typeof defaultIntegratedWebsiteSystemIds)[number];

const AllowedWebsiteTemplateSystemIdSet: ReadonlySet<number> = new Set([
    ThirdPartySystemId.GENERIC,
    ...defaultIntegratedWebsiteSystemIds,
]);

export function isAllowedWebsiteTemplateSystemId(
    systemId: number
): systemId is AllowedWebsiteTemplateSystemId {
    return AllowedWebsiteTemplateSystemIdSet.has(systemId);
}

export const AllowedWebsiteTemplateSystemIdSchema: z.ZodType<AllowedWebsiteTemplateSystemId> = z
    .number()
    .int()
    .refine(isAllowedWebsiteTemplateSystemId, 'Unsupported allowed website template system id.');

export interface AllowedWebsiteTemplateCapabilities {
    can_display: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_disable: boolean;
    can_enable: boolean;
    can_use: boolean;
    can_audit: boolean;
}

export interface AllowedWebsiteTemplateCollectionCapabilities {
    can_create: boolean;
    can_show_disabled: boolean;
}

export interface AllowedWebsiteTemplateBlastRadiusSummary {
    active_merchant_count: number;
}

export interface AllowedWebsiteTemplateListItem {
    allowed_website_template_uuid: string;
    ipx_system_id: number;
    name: string;
    status: Status;
    primary_url: string;
    website_config: UnmanagedAllowedWebsite;
    rbs_website: RbsWebsite | null;
    blast_radius: AllowedWebsiteTemplateBlastRadiusSummary;
    capabilities: AllowedWebsiteTemplateCapabilities;
}

export interface AllowedWebsiteTemplateSearchRequest extends OpaqueCursorSearchRequest {
    ipx_system_id?: number;
    status?: Status;
    show_disabled?: boolean;
}

export interface AllowedWebsiteTemplateSearchPageResponse {
    templates: AllowedWebsiteTemplateListItem[];
    pagination: OpaqueCursorPagination;
    capabilities: AllowedWebsiteTemplateCollectionCapabilities;
}

export interface CreateAllowedWebsiteTemplateRequest {
    ipx_system_id: AllowedWebsiteTemplateSystemId;
    name: string;
    website_config: UnmanagedAllowedWebsite;
    rbs_website?: RbsWebsite | null;
}

export interface UpdateAllowedWebsiteTemplateRequest {
    ipx_system_id?: AllowedWebsiteTemplateSystemId;
    name?: string;
    website_config?: UnmanagedAllowedWebsite;
    expected_website_config?: UnmanagedAllowedWebsite;
    rbs_website?: RbsWebsite | null;
    expected_active_merchant_count?: number;
}

export interface AllowedWebsiteTemplateUuidRequest {
    allowed_website_template_uuid: string;
}

export interface AllowedWebsiteTemplateMutationResponse {
    template: AllowedWebsiteTemplateListItem;
}

export interface AllowedWebsiteTemplateTransfer {
    ipx_system_id: AllowedWebsiteTemplateSystemId;
    name: string;
    website_config: UnmanagedAllowedWebsite;
    rbs_website: RbsWebsite | null;
}

export interface AllowedWebsiteTemplateTransferV1 {
    schema_version: 1;
    transfer_kind: 'allowed_website_template';
    template: {
        ipx_system_id: AllowedWebsiteTemplateSystemId;
        name: string;
        website_config: StoredUnmanagedAllowedWebsite;
        rbs_website: RbsWebsiteStorageV1 | null;
    };
}

export interface AllowedWebsiteTemplateBlastRadiusResponse {
    allowed_website_template_uuid: string;
    blast_radius: AllowedWebsiteTemplateBlastRadiusSummary;
}

export interface SafeDeleteAllowedWebsiteTemplateResponse {
    allowed_website_template_uuid: string;
    deleted: true;
}

export { OrganizationAllowedWebsiteSource } from '@pillar-portal/database/dist/enums';

export interface AllowedWebsiteTemplateSummary {
    allowed_website_template_uuid: string;
    ipx_system_id: number;
    name: string;
    status: Status;
}

export interface AllowedWebsiteTemplateProvenanceCapabilities {
    can_view_template: boolean;
}

export interface AllowedWebsiteTemplateProvenance extends AllowedWebsiteTemplateSummary {
    capabilities: AllowedWebsiteTemplateProvenanceCapabilities;
}

export interface OrganizationAllowedWebsiteCapabilities {
    can_edit: boolean;
    can_delete: boolean;
    can_unlink_template: boolean;
    can_customize_template: boolean;
    can_promote_to_template: boolean;
    can_view_template: boolean;
}

export interface OrganizationAllowedWebsiteCollectionCapabilities {
    can_create_local: boolean;
    can_reference_template: boolean;
}

export interface OrganizationAllowedWebsiteBase {
    organization_allowed_website_uuid: string;
    organization_uuid: string;
    status: Status;
    display_order: number | null;
    website_config: UnmanagedAllowedWebsite;
    rbs_website: RbsWebsite | null;
    capabilities: OrganizationAllowedWebsiteCapabilities;
}

export interface LocalOrganizationAllowedWebsite extends OrganizationAllowedWebsiteBase {
    source: typeof OrganizationAllowedWebsiteSource.LOCAL;
    template: null;
    copied_from_template: null;
    template_provenance?: AllowedWebsiteTemplateProvenance | null;
}

export interface TemplateReferenceOrganizationAllowedWebsite extends OrganizationAllowedWebsiteBase {
    source: typeof OrganizationAllowedWebsiteSource.TEMPLATE_REFERENCE;
    included_in_runtime_config: boolean;
    template: AllowedWebsiteTemplateSummary;
    copied_from_template: null;
}

export interface TemplateCopyOrganizationAllowedWebsite extends OrganizationAllowedWebsiteBase {
    source: typeof OrganizationAllowedWebsiteSource.TEMPLATE_COPY;
    template: null;
    copied_from_template: AllowedWebsiteTemplateSummary;
    template_provenance?: AllowedWebsiteTemplateProvenance | null;
}

export type OrganizationAllowedWebsite =
    | LocalOrganizationAllowedWebsite
    | TemplateReferenceOrganizationAllowedWebsite
    | TemplateCopyOrganizationAllowedWebsite;

export interface ListOrganizationAllowedWebsitesResponse {
    websites: OrganizationAllowedWebsite[];
    capabilities: OrganizationAllowedWebsiteCollectionCapabilities;
}

export interface CreateLocalOrganizationAllowedWebsiteRequest {
    website_config: UnmanagedAllowedWebsite;
    display_order?: number | null;
    rbs_website?: RbsWebsite | null;
}

export interface ReferenceAllowedWebsiteTemplateToOrganizationRequest {
    allowed_website_template_uuid: string;
    display_order?: number | null;
}

export interface UpdateOrganizationAllowedWebsiteRequest {
    website_config?: UnmanagedAllowedWebsite;
    expected_website_config?: UnmanagedAllowedWebsite;
    display_order?: number | null;
    rbs_config?: RpaBuilderWebsiteConfig;
}

export interface OrganizationAllowedWebsiteMutationResponse {
    organization_website: OrganizationAllowedWebsite;
}

export interface DeleteOrganizationAllowedWebsiteResponse {
    organization_allowed_website_uuid: string;
    deleted: true;
}

export interface PromoteOrganizationWebsiteToTemplateRequest {
    organization_allowed_website_uuid: string;
    ipx_system_id: number;
    template_name: string;
}

export interface PromoteOrganizationWebsiteToTemplateResponse {
    template: AllowedWebsiteTemplateListItem;
    organization_website: OrganizationAllowedWebsite;
}

export const AllowedWebsiteTemplateCapabilitiesSchema: z.ZodType<AllowedWebsiteTemplateCapabilities> =
    z.object({
        can_display: z.boolean(),
        can_edit: z.boolean(),
        can_delete: z.boolean(),
        can_disable: z.boolean(),
        can_enable: z.boolean(),
        can_use: z.boolean(),
        can_audit: z.boolean(),
    });

export const AllowedWebsiteTemplateCollectionCapabilitiesSchema: z.ZodType<AllowedWebsiteTemplateCollectionCapabilities> =
    z.object({
        can_create: z.boolean(),
        can_show_disabled: z.boolean(),
    });

export const AllowedWebsiteTemplateBlastRadiusSummarySchema: z.ZodType<AllowedWebsiteTemplateBlastRadiusSummary> =
    z.object({
        active_merchant_count: z.number().int().nonnegative(),
    });

export const AllowedWebsiteTemplateListItemSchema: z.ZodType<AllowedWebsiteTemplateListItem> =
    z.object({
        allowed_website_template_uuid: z.string().uuid(),
        ipx_system_id: z.number().int().nonnegative(),
        name: z.string().trim().min(1),
        status: z.enum(Status),
        primary_url: z.string().url(),
        website_config: AllowedWebsiteSchemaUnmanaged,
        rbs_website: RbsWebsiteSchema.nullable(),
        blast_radius: AllowedWebsiteTemplateBlastRadiusSummarySchema,
        capabilities: AllowedWebsiteTemplateCapabilitiesSchema,
    });

export const AllowedWebsiteTemplateSearchRequestSchema: z.ZodType<AllowedWebsiteTemplateSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: AuthorizationOpaqueCursorQuerySchema,
            ipx_system_id: z.number().int().nonnegative().optional(),
            status: z.enum(Status).optional(),
            show_disabled: z.boolean().optional(),
        })
        .strict();

export const AllowedWebsiteTemplateSearchPageResponseSchema: z.ZodType<AllowedWebsiteTemplateSearchPageResponse> =
    z.object({
        templates: z.array(AllowedWebsiteTemplateListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
        capabilities: AllowedWebsiteTemplateCollectionCapabilitiesSchema,
    });

export const CreateAllowedWebsiteTemplateRequestSchema: z.ZodType<CreateAllowedWebsiteTemplateRequest> =
    z
        .object({
            ipx_system_id: AllowedWebsiteTemplateSystemIdSchema,
            name: z.string().trim().min(1),
            website_config: AllowedWebsiteWriteSchemaUnmanaged,
            rbs_website: RbsWebsiteSchema.nullable().optional(),
        })
        .strict();

export const UpdateAllowedWebsiteTemplateRequestSchema: z.ZodType<UpdateAllowedWebsiteTemplateRequest> =
    z
        .object({
            ipx_system_id: AllowedWebsiteTemplateSystemIdSchema.optional(),
            name: z.string().trim().min(1).optional(),
            website_config: AllowedWebsiteWriteSchemaUnmanaged.optional(),
            expected_website_config: AllowedWebsiteSchemaUnmanaged.optional(),
            rbs_website: RbsWebsiteSchema.nullable().optional(),
            expected_active_merchant_count: z.number().int().nonnegative().optional(),
        })
        .strict()
        .refine(
            (request) =>
                request.ipx_system_id !== undefined ||
                request.name !== undefined ||
                request.website_config !== undefined ||
                request.rbs_website !== undefined,
            'At least one template field must be updated.'
        )
        .refine(
            (request) =>
                (request.website_config === undefined) ===
                (request.expected_website_config === undefined),
            {
                message: 'website_config and expected_website_config must be supplied together.',
                path: ['expected_website_config'],
            }
        );

export const AllowedWebsiteTemplateUuidRequestSchema: z.ZodType<AllowedWebsiteTemplateUuidRequest> =
    z.object({ allowed_website_template_uuid: z.string().uuid() }).strict();
export const EnableAllowedWebsiteTemplateRequestSchema = AllowedWebsiteTemplateUuidRequestSchema;
export const DisableAllowedWebsiteTemplateRequestSchema = AllowedWebsiteTemplateUuidRequestSchema;
export const SafeDeleteAllowedWebsiteTemplateRequestSchema =
    AllowedWebsiteTemplateUuidRequestSchema;

export const AllowedWebsiteTemplateMutationResponseSchema: z.ZodType<AllowedWebsiteTemplateMutationResponse> =
    z.object({ template: AllowedWebsiteTemplateListItemSchema });

export const AllowedWebsiteTemplateTransferV1Schema: z.ZodType<AllowedWebsiteTemplateTransferV1> = z
    .object({
        schema_version: z.literal(1),
        transfer_kind: z.literal('allowed_website_template'),
        template: z
            .object({
                ipx_system_id: AllowedWebsiteTemplateSystemIdSchema,
                name: z.string().trim().min(1),
                website_config: StoredUnmanagedAllowedWebsiteStorageV2Schema,
                rbs_website: StoredRbsWebsiteStorageV1Schema.nullable(),
            })
            .strict(),
    })
    .strict();

export const AllowedWebsiteTemplateTransferContract = defineStoredJsonContract({
    contractName: 'AllowedWebsiteTemplateTransfer',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'AllowedWebsiteTemplateTransferV1Schema',
            schema: AllowedWebsiteTemplateTransferV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['schema_version'],
                    markerValue: 1,
                },
            ],
            normalize: (
                value: AllowedWebsiteTemplateTransferV1
            ): AllowedWebsiteTemplateTransfer => ({
                ipx_system_id: value.template.ipx_system_id,
                name: value.template.name,
                website_config: StoredUnmanagedAllowedWebsiteParser.parse(
                    value.template.website_config
                ),
                rbs_website:
                    value.template.rbs_website === null
                        ? null
                        : StoredRbsWebsiteParser.parse(value.template.rbs_website),
            }),
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const AllowedWebsiteTemplateTransferSchema: z.ZodType<AllowedWebsiteTemplateTransfer> =
    buildStoredJsonValueParser(AllowedWebsiteTemplateTransferContract);

export function serializeAllowedWebsiteTemplateTransferV1(
    value: AllowedWebsiteTemplateTransfer
): AllowedWebsiteTemplateTransferV1 {
    return AllowedWebsiteTemplateTransferV1Schema.parse({
        schema_version: 1,
        transfer_kind: 'allowed_website_template',
        template: {
            ipx_system_id: value.ipx_system_id,
            name: value.name,
            website_config: serializeUnmanagedAllowedWebsiteV2(value.website_config),
            rbs_website:
                value.rbs_website === null ? null : serializeRbsWebsiteV1(value.rbs_website),
        },
    });
}
export const AllowedWebsiteTemplateBlastRadiusResponseSchema: z.ZodType<AllowedWebsiteTemplateBlastRadiusResponse> =
    z.object({
        allowed_website_template_uuid: z.string().uuid(),
        blast_radius: AllowedWebsiteTemplateBlastRadiusSummarySchema,
    });
export const SafeDeleteAllowedWebsiteTemplateResponseSchema: z.ZodType<SafeDeleteAllowedWebsiteTemplateResponse> =
    z.object({
        allowed_website_template_uuid: z.string().uuid(),
        deleted: z.literal(true),
    });

export const OrganizationAllowedWebsiteSourceSchema: z.ZodType<OrganizationAllowedWebsiteSource> =
    z.enum(OrganizationAllowedWebsiteSource);

export const AllowedWebsiteTemplateSummarySchema = z.object({
    allowed_website_template_uuid: z.string().uuid(),
    ipx_system_id: z.number().int().nonnegative(),
    name: z.string().trim().min(1),
    status: z.enum(Status),
}) satisfies z.ZodType<AllowedWebsiteTemplateSummary>;

export const AllowedWebsiteTemplateProvenanceCapabilitiesSchema: z.ZodType<AllowedWebsiteTemplateProvenanceCapabilities> =
    z.object({ can_view_template: z.boolean() });

export const AllowedWebsiteTemplateProvenanceSchema: z.ZodType<AllowedWebsiteTemplateProvenance> =
    AllowedWebsiteTemplateSummarySchema.extend({
        capabilities: AllowedWebsiteTemplateProvenanceCapabilitiesSchema,
    });

export const OrganizationAllowedWebsiteCapabilitiesSchema: z.ZodType<OrganizationAllowedWebsiteCapabilities> =
    z.object({
        can_edit: z.boolean(),
        can_delete: z.boolean(),
        can_unlink_template: z.boolean(),
        can_customize_template: z.boolean(),
        can_promote_to_template: z.boolean(),
        can_view_template: z.boolean(),
    });

export const OrganizationAllowedWebsiteCollectionCapabilitiesSchema: z.ZodType<OrganizationAllowedWebsiteCollectionCapabilities> =
    z.object({
        can_create_local: z.boolean(),
        can_reference_template: z.boolean(),
    });

const OrganizationAllowedWebsiteBaseSchema = z.object({
    organization_allowed_website_uuid: z.string().uuid(),
    organization_uuid: z.string().uuid(),
    status: z.enum(Status),
    display_order: z.number().int().nonnegative().nullable(),
    website_config: AllowedWebsiteSchemaUnmanaged,
    rbs_website: RbsWebsiteSchema.nullable(),
    capabilities: OrganizationAllowedWebsiteCapabilitiesSchema,
});

export const LocalOrganizationAllowedWebsiteSchema = OrganizationAllowedWebsiteBaseSchema.extend({
    source: z.literal(OrganizationAllowedWebsiteSource.LOCAL),
    template: z.null(),
    copied_from_template: z.null(),
    template_provenance: AllowedWebsiteTemplateProvenanceSchema.nullable().optional(),
});

export const TemplateReferenceOrganizationAllowedWebsiteSchema =
    OrganizationAllowedWebsiteBaseSchema.extend({
        source: z.literal(OrganizationAllowedWebsiteSource.TEMPLATE_REFERENCE),
        included_in_runtime_config: z.boolean(),
        template: AllowedWebsiteTemplateSummarySchema,
        copied_from_template: z.null(),
    });

export const TemplateCopyOrganizationAllowedWebsiteSchema =
    OrganizationAllowedWebsiteBaseSchema.extend({
        source: z.literal(OrganizationAllowedWebsiteSource.TEMPLATE_COPY),
        template: z.null(),
        copied_from_template: AllowedWebsiteTemplateSummarySchema,
        template_provenance: AllowedWebsiteTemplateProvenanceSchema.nullable().optional(),
    });

export const OrganizationAllowedWebsiteSchema: z.ZodType<OrganizationAllowedWebsite> =
    z.discriminatedUnion('source', [
        LocalOrganizationAllowedWebsiteSchema,
        TemplateReferenceOrganizationAllowedWebsiteSchema,
        TemplateCopyOrganizationAllowedWebsiteSchema,
    ]);

export const ListOrganizationAllowedWebsitesResponseSchema: z.ZodType<ListOrganizationAllowedWebsitesResponse> =
    z.object({
        websites: z.array(OrganizationAllowedWebsiteSchema),
        capabilities: OrganizationAllowedWebsiteCollectionCapabilitiesSchema,
    });

export const CreateLocalOrganizationAllowedWebsiteRequestSchema: z.ZodType<CreateLocalOrganizationAllowedWebsiteRequest> =
    z
        .object({
            website_config: AllowedWebsiteWriteSchemaUnmanaged,
            display_order: z.number().int().nonnegative().nullable().optional(),
            rbs_website: RbsWebsiteSchema.nullable().optional(),
        })
        .strict();

export const ReferenceAllowedWebsiteTemplateToOrganizationRequestSchema: z.ZodType<ReferenceAllowedWebsiteTemplateToOrganizationRequest> =
    z
        .object({
            allowed_website_template_uuid: z.string().uuid(),
            display_order: z.number().int().nonnegative().nullable().optional(),
        })
        .strict();

export const UpdateOrganizationAllowedWebsiteRequestSchema: z.ZodType<UpdateOrganizationAllowedWebsiteRequest> =
    z
        .object({
            website_config: AllowedWebsiteWriteSchemaUnmanaged.optional(),
            expected_website_config: AllowedWebsiteSchemaUnmanaged.optional(),
            display_order: z.number().int().nonnegative().nullable().optional(),
            rbs_config: RpaBuilderWebsiteConfigSchema.optional(),
        })
        .strict()
        .refine(
            (request) =>
                request.website_config !== undefined ||
                request.display_order !== undefined ||
                request.rbs_config !== undefined,
            'At least one organization website field must be updated.'
        )
        .refine(
            (request) =>
                (request.website_config === undefined) ===
                (request.expected_website_config === undefined),
            {
                message: 'website_config and expected_website_config must be supplied together.',
                path: ['expected_website_config'],
            }
        );

export const OrganizationAllowedWebsiteMutationResponseSchema: z.ZodType<OrganizationAllowedWebsiteMutationResponse> =
    z.object({ organization_website: OrganizationAllowedWebsiteSchema });

export const DeleteOrganizationAllowedWebsiteResponseSchema: z.ZodType<DeleteOrganizationAllowedWebsiteResponse> =
    z.object({
        organization_allowed_website_uuid: z.string().uuid(),
        deleted: z.literal(true),
    });

export const PromoteOrganizationWebsiteToTemplateRequestSchema: z.ZodType<PromoteOrganizationWebsiteToTemplateRequest> =
    z
        .object({
            organization_allowed_website_uuid: z.string().uuid(),
            ipx_system_id: z.number().int().nonnegative(),
            template_name: z.string().trim().min(1),
        })
        .strict();

export const PromoteOrganizationWebsiteToTemplateResponseSchema: z.ZodType<PromoteOrganizationWebsiteToTemplateResponse> =
    z.object({
        template: AllowedWebsiteTemplateListItemSchema,
        organization_website: OrganizationAllowedWebsiteSchema,
    });
